import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { RFP } from "@/constants/testIds";
import { AUDIENCE_TYPES, ASSET_CLASSES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RFPForm = ({ dark = false }) => {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", investor_type: "", asset_class: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please provide your name, email and a brief message.");
      return;
    }
    setLoading(true);
    try {
      const property_type = [form.investor_type, form.asset_class].filter(Boolean).join(" · ");
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        investor_type: form.investor_type,
        asset_class: form.asset_class,
        property_type,
        message: form.message,
      });
      setDone(true);
      toast.success("Received. A member of our team will respond within one business day.");
      setForm({ name: "", company: "", email: "", phone: "", investor_type: "", asset_class: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const label = `overline mb-2 block ${dark ? "text-[#8FB8A2]" : "text-[#5C6660]"}`;
  const field = `w-full bg-transparent border-b py-3 text-[15px] focus:outline-none transition-colors duration-300 ${
    dark ? "border-white/20 text-white placeholder:text-white/40 focus:border-[#8FB8A2]" : "border-[#D8D8D0] text-[#121A16] placeholder:text-[#A0A69F] focus:border-[#1B3B2B]"
  }`;

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className={`p-10 border rounded-sm ${dark ? "border-white/15" : "border-[#E5E5DF] bg-white"}`}>
        <CheckCircle2 className={`w-10 h-10 mb-5 ${dark ? "text-[#8FB8A2]" : "text-[#275C43]"}`} strokeWidth={1.4} />
        <h3 className={`font-display text-3xl mb-3 ${dark ? "text-white" : "text-[#121A16]"}`}>Thank you.</h3>
        <p className={dark ? "text-white/60" : "text-[#5C6660]"}>
          Your inquiry has reached our team. We will respond within one business day to discuss your portfolio and objectives.
        </p>
        <button onClick={() => setDone(false)} className={`mt-6 text-sm ${dark ? "text-[#8FB8A2]" : "text-[#275C43]"} hover:underline`}>
          Submit another inquiry
        </button>
      </motion.div>
    );
  }

  return (
    <form data-testid={RFP.form} onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
      <div>
        <label htmlFor="rfp-name" className={label}>Full Name</label>
        <input id="rfp-name" name="name" autoComplete="name" aria-label="Full name" data-testid={RFP.name} value={form.name} onChange={update("name")} placeholder="John Smith" className={field} />
      </div>
      <div>
        <label htmlFor="rfp-company" className={label}>Company / Fund</label>
        <input id="rfp-company" name="organization" autoComplete="organization" aria-label="Company or fund" data-testid={RFP.company} value={form.company} onChange={update("company")} placeholder="Organization" className={field} />
      </div>
      <div>
        <label htmlFor="rfp-email" className={label}>Email</label>
        <input id="rfp-email" name="email" type="email" autoComplete="email" aria-label="Email address" data-testid={RFP.email} value={form.email} onChange={update("email")} placeholder="name@email.com" className={field} />
      </div>
      <div>
        <label htmlFor="rfp-phone" className={label}>Phone</label>
        <input id="rfp-phone" name="phone" autoComplete="tel" aria-label="Phone number" data-testid={RFP.phone} value={form.phone} onChange={update("phone")} placeholder="555-555-5555" className={field} />
      </div>
      <div>
        <label htmlFor="rfp-investor-type" className={label}>Investor Type</label>
        <select id="rfp-investor-type" aria-label="Investor type" data-testid={RFP.investorType} value={form.investor_type} onChange={update("investor_type")} className={`${field} appearance-none`}>
          <option value="" className="text-black">Select…</option>
          {AUDIENCE_TYPES.map((t) => <option key={t} value={t} className="text-black">{t}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="rfp-asset-class" className={label}>Primary Asset Class</label>
        <select id="rfp-asset-class" aria-label="Primary asset class" data-testid={RFP.assetClass} value={form.asset_class} onChange={update("asset_class")} className={`${field} appearance-none`}>
          <option value="" className="text-black">Select…</option>
          {ASSET_CLASSES.map((a) => <option key={a.slug} value={a.name} className="text-black">{a.name}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="rfp-message" className={label}>How can we help?</label>
        <textarea id="rfp-message" aria-label="Message" data-testid={RFP.message} value={form.message} onChange={update("message")} rows={4}
          placeholder="Portfolio size, locations, current management, and objectives." className={`${field} resize-none`} />
      </div>
      <div className="sm:col-span-2">
        <button data-testid={RFP.submit} type="submit" disabled={loading}
          className={`group inline-flex items-center gap-3 rounded-sm px-8 py-4 text-sm font-medium transition-colors duration-300 disabled:opacity-60 ${
            dark ? "bg-white text-[#1B3B2B] hover:bg-[#E8E2D9]" : "bg-[#1B3B2B] text-white hover:bg-[#275C43]"
          }`}>
          {loading ? "Submitting…" : "Submit Inquiry"}
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </form>
  );
};
